// src/hooks/useReps.ts
//
// React data hook for the rep roster. Plain useState/useCallback/useEffect
// per the repo's hook style (no React Query); all data access goes through
// repsService. Like useRegions, a post-import refetch is silent — its
// failure must never surface as the page-level error, because the import
// itself already succeeded on the server (Story 1.1 review lesson). No
// realtime subscription: a single-admin editor is served by refetch after
// the mutation.

import { useState, useEffect, useCallback } from 'react'
import { repsService, type RepEditFields } from '../services/repsService'
import type { YakRep } from '../types/yak'
import type { CandidateRep, ImportReport } from '../utils/repImport'

interface UseRepsReturn {
  reps: YakRep[]
  loading: boolean
  error: string | null
  refetch: () => Promise<void>
  importCsv: (candidates: CandidateRep[]) => Promise<ImportReport>
  update: (id: string, fields: RepEditFields) => Promise<void>
  deactivate: (id: string) => Promise<void>
  reactivate: (id: string) => Promise<void>
}

export function useReps(): UseRepsReturn {
  const [reps, setReps] = useState<YakRep[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchReps = useCallback(async (silent = false) => {
    try {
      if (!silent) setLoading(true)
      setError(null)
      setReps(await repsService.listReps())
    } catch (err) {
      // A silent refresh runs after an import that already succeeded on the
      // server. Surfacing its failure as the page-level banner would
      // contradict that success; only the initial/explicit load owns the
      // banner. A failed background refresh leaves the last-known roster in
      // place and reconciles on the next successful fetch.
      if (!silent) {
        setError(err instanceof Error ? err.message : 'Failed to load reps')
      } else {
        console.error('Rep list refresh failed', err)
      }
    } finally {
      if (!silent) setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchReps()
  }, [fetchReps])

  // Returns the import report to the caller so the screen can render it, then
  // silently refetches so the roster table reflects the accepted rows.
  const importCsv = useCallback(
    async (candidates: CandidateRep[]): Promise<ImportReport> => {
      const report = await repsService.importReps(candidates)
      await fetchReps(true)
      return report
    },
    [fetchReps]
  )

  // Edit/deactivate/reactivate all follow the mutation-then-silent-refetch
  // shape: the service throws mapped copy on failure (surfaced by the form),
  // and a successful write is reconciled by a silent refetch that never
  // raises the page banner (Story 1.1 silent-refresh lesson, already applied
  // to importCsv above). No optimistic updates — the table re-renders from
  // the server's answer only.
  const update = useCallback(
    async (id: string, fields: RepEditFields) => {
      await repsService.updateRep(id, fields)
      await fetchReps(true)
    },
    [fetchReps]
  )

  const deactivate = useCallback(
    async (id: string) => {
      await repsService.setRepActive(id, false)
      await fetchReps(true)
    },
    [fetchReps]
  )

  const reactivate = useCallback(
    async (id: string) => {
      await repsService.setRepActive(id, true)
      await fetchReps(true)
    },
    [fetchReps]
  )

  return {
    reps,
    loading,
    error,
    refetch: () => fetchReps(),
    importCsv,
    update,
    deactivate,
    reactivate,
  }
}
